import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import { blogAPI } from '../services/api';
import { BlogPost } from '../types';
import { FaClock, FaCalendar, FaUser, FaArrowLeft, FaArrowRight, FaTag } from 'react-icons/fa';
import DynamicButton from '../components/dynamic/DynamicButton';
import DynamicTag from '../components/dynamic/DynamicTag';
import { parseMarkdownSections } from '../utils/markdownSections';

const BlogDetail: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const [post, setPost] = useState<BlogPost | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [currentSectionIndex, setCurrentSectionIndex] = useState(0);

  const sections = useMemo(() => 
    post ? parseMarkdownSections(post.content) : [], 
    [post]
  );
  const hasMultipleSections = sections.length > 1;
  const currentSection = sections[currentSectionIndex];

  const goToSection = (index: number) => {
    setCurrentSectionIndex(index);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  useEffect(() => {
    const loadPost = async () => {
      if (!slug) {
        setError('Invalid blog post');
        setLoading(false);
        return;
      }

      try {
        const response = await blogAPI.getBySlug(slug);
        if (response && response.data) {
          setPost(response.data);
        } else {
          setError('Blog post not found');
        }
      } catch (error: any) {
        console.error('Error loading blog post:', error);
        setError('Failed to load blog post');
      } finally {
        setLoading(false);
      }
    };

    loadPost();
  }, [slug]);

  useEffect(() => {
    setCurrentSectionIndex(0);
  }, [slug]);

  if (loading) {
    return (
      <div className="pt-20 min-h-screen flex items-center justify-center bg-white dark:bg-gray-900">
        <div className="text-xl text-gray-600 dark:text-gray-400">Loading blog post...</div>
      </div>
    );
  }

  if (error || !post) {
    return (
      <div className="pt-20 min-h-screen flex items-center justify-center bg-white dark:bg-gray-900">
        <div className="text-center">
          <div className="text-xl text-red-600 dark:text-red-400 mb-4">{error || 'Blog post not found'}</div>
          <DynamicButton variant="outline" onClick={() => navigate('/blog')}>
            <FaArrowLeft className="mr-2 inline" />
            Back to Blog
          </DynamicButton>
        </div>
      </div>
    );
  }

  return (
    <div className="pt-20 bg-white dark:bg-gray-900 min-h-screen">
      <div className="max-w-4xl mx-auto px-4 py-8">
        {/* Back Button */}
        <DynamicButton
          variant="outline"
          onClick={() => navigate('/blog')}
          className="mb-6"
        >
          <FaArrowLeft className="mr-2 inline" />
          Back to Blog
        </DynamicButton>

        {/* Header */}
        <div className="mb-8">
          {post.imageUrl && (
            <img
              src={post.imageUrl}
              alt={post.title}
              className="w-full h-64 md:h-96 object-cover rounded-lg mb-6"
            />
          )}
          
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 dark:text-white mb-4">
            {post.title}
          </h1>

          <div className="flex flex-wrap items-center gap-4 text-sm text-gray-600 dark:text-gray-400 mb-4">
            <span className="flex items-center">
              <FaUser className="mr-2" />
              {post.author}
            </span>
            <span className="flex items-center">
              <FaCalendar className="mr-2" />
              {new Date(post.publishedAt).toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'long',
                day: 'numeric'
              })}
            </span>
            <span className="flex items-center">
              <FaClock className="mr-2" />
              {post.readTime} min read
            </span>
            {post.category && (
              <span className="px-3 py-1 bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200 rounded-full text-xs font-semibold">
                {post.category}
              </span>
            )}
          </div>

          {post.tags && post.tags.length > 0 && (
            <div className="flex flex-wrap gap-2 mb-6">
              {post.tags.map((tag) => (
                <DynamicTag key={tag} variant="primary" size="sm">
                  <FaTag className="mr-1 inline text-xs" />
                  {tag}
                </DynamicTag>
              ))}
            </div>
          )}

          {post.excerpt && (
            <p className="text-xl text-gray-700 dark:text-gray-300 italic border-l-4 border-blue-500 pl-4 py-2">
              {post.excerpt}
            </p>
          )}
        </div>

        {/* Content */}
        <div className="prose prose-lg dark:prose-invert max-w-none">
          <ReactMarkdown
            components={{
              h1: ({ node, ...props }) => <h1 className="text-4xl font-bold mt-8 mb-4 text-gray-900 dark:text-white" {...props} />,
              h2: ({ node, ...props }) => <h2 className="text-3xl font-bold mt-6 mb-3 text-gray-900 dark:text-white" {...props} />,
              h3: ({ node, ...props }) => <h3 className="text-2xl font-bold mt-4 mb-2 text-gray-900 dark:text-white" {...props} />,
              h4: ({ node, ...props }) => <h4 className="text-xl font-bold mt-3 mb-2 text-gray-900 dark:text-white" {...props} />,
              p: ({ node, ...props }) => <p className="mb-4 text-gray-700 dark:text-gray-300 leading-relaxed" {...props} />,
              ul: ({ node, ...props }) => <ul className="list-disc list-inside mb-4 space-y-2 text-gray-700 dark:text-gray-300" {...props} />,
              ol: ({ node, ...props }) => <ol className="list-decimal list-inside mb-4 space-y-2 text-gray-700 dark:text-gray-300" {...props} />,
              li: ({ node, ...props }) => <li className="ml-4" {...props} />,
              code: ({ node, inline, ...props }: any) => 
                inline ? (
                  <code className="bg-gray-100 dark:bg-gray-800 px-2 py-1 rounded text-sm font-mono text-red-600 dark:text-red-400" {...props} />
                ) : (
                  <code className="block bg-gray-100 dark:bg-gray-800 p-4 rounded-lg overflow-x-auto text-sm font-mono mb-4" {...props} />
                ),
              pre: ({ node, ...props }) => <pre className="bg-gray-100 dark:bg-gray-800 p-4 rounded-lg overflow-x-auto mb-4" {...props} />,
              blockquote: ({ node, ...props }) => (
                <blockquote className="border-l-4 border-blue-500 pl-4 italic my-4 text-gray-700 dark:text-gray-300" {...props} />
              ),
              a: ({ node, ...props }) => (
                <a className="text-blue-600 dark:text-blue-400 hover:underline" {...props} />
              ),
              strong: ({ node, ...props }) => <strong className="font-bold text-gray-900 dark:text-white" {...props} />,
              em: ({ node, ...props }) => <em className="italic" {...props} />,
              img: ({ node, ...props }: any) => (
                <img 
                  className="w-full rounded-lg shadow-lg my-6 border border-gray-200 dark:border-gray-700" 
                  alt={props.alt || 'Blog image'}
                  {...props} 
                />
              ),
            }}
          >
            {hasMultipleSections && currentSection
              ? currentSection.content
              : post.content}
          </ReactMarkdown>
        </div>

        {/* Section navigation (bottom) */}
        {hasMultipleSections && (
          <div className="mt-8 pt-6 border-t border-gray-200 dark:border-gray-700 flex items-center justify-between gap-4">
            {currentSectionIndex > 0 ? (
              <button
                onClick={() => goToSection(currentSectionIndex - 1)}
                className="inline-flex items-center gap-1.5 px-3 py-2 text-xs font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 rounded-lg border border-gray-200 dark:border-gray-700 whitespace-nowrap max-w-[45%] min-w-0"
              >
                <FaArrowLeft className="flex-shrink-0" />
                <span className="truncate">
                  {sections[currentSectionIndex - 1].title.length > 30
                    ? sections[currentSectionIndex - 1].title.slice(0, 27) + '...'
                    : sections[currentSectionIndex - 1].title}
                </span>
              </button>
            ) : (
              <div />
            )}
            {currentSectionIndex < sections.length - 1 ? (
              <button
                onClick={() => goToSection(currentSectionIndex + 1)}
                className="inline-flex items-center gap-1.5 px-3 py-2 text-xs font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 rounded-lg border border-gray-200 dark:border-gray-700 whitespace-nowrap max-w-[45%] min-w-0 ml-auto"
              >
                <span className="truncate">
                  {sections[currentSectionIndex + 1].title.length > 30
                    ? sections[currentSectionIndex + 1].title.slice(0, 27) + '...'
                    : sections[currentSectionIndex + 1].title}
                </span>
                <FaArrowRight className="flex-shrink-0" />
              </button>
            ) : (
              <div />
            )}
          </div>
        )}

        {/* Footer */}
        <div className="mt-12 pt-8 border-t border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between">
            <div className="text-sm text-gray-600 dark:text-gray-400">
              {post.updatedAt && (
                <span>Last updated: {new Date(post.updatedAt).toLocaleDateString()}</span>
              )}
            </div>
            <DynamicButton variant="outline" onClick={() => navigate('/blog')}>
              <FaArrowLeft className="mr-2 inline" />
              Back to Blog
            </DynamicButton>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BlogDetail;
